import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import {
    Plus,
    Trash2,
    Package,
    AlertCircle,
    Loader
} from 'lucide-react';
import { purchasesAPI, suppliersAPI, locationsAPI, productsAPI, zonesAPI, inventoriesAPI } from '../../services/garageApi';

const CreatePurchase = () => {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Product search state — replaces the old bulk-loaded `products` array
    const [productSearch, setProductSearch] = useState('');
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const debounceRef = useRef(null);

    // Map of "type-productId" → inventory records (for location/zone filtering)
    const [productInventoryMap, setProductInventoryMap] = useState({});

    const [formData, setFormData] = useState({
        supplier_id: '',
        location_id: '',
        zone_id: '',
        purchase_date: new Date().toISOString().split('T')[0],
        items: []
    });

    const [newItem, setNewItem] = useState({
        product_type: '',
        product_id: '',
        product_name: '',
        quantity: 1,
        cost_price: 0,
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [suppliersRes, locationsRes] = await Promise.all([
                suppliersAPI.getAll(),
                locationsAPI.getAll()
            ]);

            setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : suppliersRes.data?.data || []);
            setLocations(Array.isArray(locationsRes.data) ? locationsRes.data : locationsRes.data?.data || []);
        } catch (err) {
            setError('Failed to load form data. Please refresh.');
        } finally {
            setLoading(false);
        }
    };

    // Debounced product search — fires when the search term or selected type changes
    useEffect(() => {
        clearTimeout(debounceRef.current);

        if (!productSearch && !newItem.product_type) {
            setProductSearchResults([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setProductSearchLoading(true);
            try {
                const params = {};
                if (productSearch.length >= 2) params.search = productSearch;
                if (newItem.product_type) params.category = newItem.product_type;

                const res = await productsAPI.getAll(params);
                setProductSearchResults(Array.isArray(res.data) ? res.data : []);
            } catch {
                setProductSearchResults([]);
            } finally {
                setProductSearchLoading(false);
            }
        }, 350);

        return () => clearTimeout(debounceRef.current);
    }, [productSearch, newItem.product_type]);

    // Fetch zones when location changes
    const fetchZones = useCallback(async (locationId) => {
        if (!locationId) {
            setZones([]);
            return;
        }
        try {
            const response = await zonesAPI.getAll({ location_id: locationId, per_page: 100 });
            const zonesData = Array.isArray(response.data) ? response.data : [];
            // Exclude system (Unallocated) zones from the dropdown
            setZones(zonesData.filter(z => !z.is_system));
        } catch {
            setZones([]);
        }
    }, []);

    useEffect(() => {
        fetchZones(formData.location_id);
        setFormData(prev => ({ ...prev, zone_id: '' }));
    }, [formData.location_id, fetchZones]);

    // Fetch inventory data for each product in items (to filter locations/zones)
    useEffect(() => {
        const fetchInventories = async () => {
            const newMap = { ...productInventoryMap };
            let changed = false;

            for (const item of formData.items) {
                const key = `${item.product_type}-${item.product_id}`;
                if (newMap[key] === undefined) {
                    try {
                        const res = await inventoriesAPI.getByProduct(item.product_type, item.product_id);
                        newMap[key] = Array.isArray(res.data) ? res.data : [];
                    } catch {
                        newMap[key] = [];
                    }
                    changed = true;
                }
            }

            if (changed) setProductInventoryMap(newMap);
        };

        if (formData.items.length > 0) {
            fetchInventories();
        }
    }, [formData.items]); // eslint-disable-line react-hooks/exhaustive-deps

    // Compute available locations based on items' inventory
    const availableLocations = useMemo(() => {
        if (formData.items.length === 0) return locations;

        const locationIds = new Set();
        let anyNewProduct = false;

        for (const item of formData.items) {
            const key = `${item.product_type}-${item.product_id}`;
            const inventories = productInventoryMap[key];
            if (!inventories || inventories.length === 0) {
                // Product has no inventory anywhere — it's new, show all locations
                anyNewProduct = true;
                break;
            }
            for (const inv of inventories) {
                if (inv.location_id) locationIds.add(String(inv.location_id));
            }
        }

        if (anyNewProduct) return locations;
        return locations.filter(loc => locationIds.has(String(loc.id)));
    }, [formData.items, productInventoryMap, locations]);

    // Compute available zones for the selected location based on items' inventory
    const availableZones = useMemo(() => {
        if (!formData.location_id) return [];
        if (formData.items.length === 0) return zones;

        const zoneIds = new Set();
        let anyNewProduct = false;

        for (const item of formData.items) {
            const key = `${item.product_type}-${item.product_id}`;
            const inventories = productInventoryMap[key];
            if (!inventories || inventories.length === 0) {
                anyNewProduct = true;
                break;
            }
            for (const inv of inventories) {
                if (String(inv.location_id) === String(formData.location_id) && inv.zone_id) {
                    zoneIds.add(String(inv.zone_id));
                }
            }
        }

        if (anyNewProduct) return zones;
        return zones.filter(z => zoneIds.has(String(z.id)));
    }, [formData.location_id, formData.items, productInventoryMap, zones]);

    // Reset location if it's no longer in the available list
    useEffect(() => {
        if (formData.location_id && formData.items.length > 0) {
            const isAvailable = availableLocations.some(loc => String(loc.id) === String(formData.location_id));
            if (!isAvailable) {
                setFormData(prev => ({ ...prev, location_id: '', zone_id: '' }));
            }
        }
    }, [availableLocations]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (e) => {
        const { name, value } = e.target;
        setNewItem(prev => ({ ...prev, [name]: value }));

        // Reset product search + selection when type changes
        if (name === 'product_type') {
            setNewItem(prev => ({ ...prev, product_id: '', product_name: '', cost_price: 0 }));
            setProductSearch('');
            setProductSearchResults([]);
        }
    };

    // Called when the user picks a product from the search results dropdown
    const selectProduct = (product) => {
        setNewItem(prev => ({
            ...prev,
            product_id: product.id,
            product_name: product.name,
            cost_price: product.cost_price || 0,
        }));
        setProductSearch(product.name);
        setProductSearchResults([]);
    };

    const addItem = () => {
        if (!newItem.product_type || !newItem.product_id) {
            setError('Please select a product type and product.');
            return;
        }
        if (!newItem.quantity || newItem.quantity < 1) {
            setError('Quantity must be at least 1.');
            return;
        }

        setError('');
        const total_price = Number(newItem.cost_price) * Number(newItem.quantity);

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                ...newItem,
                total_price
            }]
        }));

        setNewItem({
            product_type: '',
            product_id: '',
            product_name: '',
            quantity: 1,
            cost_price: 0,
            notes: ''
        });
        setProductSearch('');
        setProductSearchResults([]);
    };

    const removeItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const subtotal = formData.items.reduce((sum, item) => sum + (item.total_price || 0), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.items.length === 0) {
            setError('Please add at least one item.');
            return;
        }

        try {
            setSubmitting(true);
            await purchasesAPI.create({
                supplier_id: formData.supplier_id,
                location_id: formData.location_id,
                zone_id: formData.zone_id || undefined,
                purchase_date: formData.purchase_date,
                items: formData.items.map(item => ({
                    product_type: item.product_type,
                    product_id: item.product_id,
                    quantity: Number(item.quantity),
                    cost_price: Number(item.cost_price),
                    notes: item.notes || undefined
                }))
            });
            navigate('/purchases');
        } catch (err) {
            const message = err.response?.data?.error || err.response?.data?.message || 'Error creating purchase order. Please try again.';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center">
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Loading...
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <BackButton to="/purchases" />
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Create Purchase Order</h1>
                    <p className="text-sm text-gray-500">Add a new purchase from a supplier</p>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                    <div className="flex items-center space-x-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Purchase Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Supplier *</label>
                            <select
                                name="supplier_id"
                                value={formData.supplier_id}
                                onChange={handleChange}
                                className="form-select w-full rounded-md border-gray-300"
                                required
                            >
                                <option value="">Select Supplier</option>
                                {suppliers.map(supplier => (
                                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date *</label>
                            <input
                                type="date"
                                name="purchase_date"
                                value={formData.purchase_date}
                                onChange={handleChange}
                                className="form-input w-full rounded-md border-gray-300"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                            <select
                                name="location_id"
                                value={formData.location_id}
                                onChange={handleChange}
                                className="form-select w-full rounded-md border-gray-300"
                                required
                            >
                                <option value="">Select Location</option>
                                {availableLocations.map(location => (
                                    <option key={location.id} value={location.id}>{location.name}</option>
                                ))}
                            </select>
                            {formData.items.length > 0 && availableLocations.length === 0 && (
                                <p className="mt-1 text-xs text-yellow-600">No locations have inventory for the selected items.</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Zone</label>
                            <select
                                name="zone_id"
                                value={formData.zone_id}
                                onChange={handleChange}
                                className="form-select w-full rounded-md border-gray-300"
                                disabled={!formData.location_id}
                            >
                                <option value="">Select Zone (optional)</option>
                                {availableZones.map(zone => (
                                    <option key={zone.id} value={zone.id}>{zone.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Add Items Section */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                        <Package className="h-4 w-4 mr-2" />
                        Purchase Items
                    </h3>

                    {/* New Item Row */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                            <select
                                name="product_type"
                                value={newItem.product_type}
                                onChange={handleItemChange}
                                className="form-select w-full rounded-md border-gray-300 text-sm"
                            >
                                <option value="">Select Type</option>
                                <option value="tire">Tire</option>
                                <option value="battery">Battery</option>
                                <option value="wiper">Wiper</option>
                            </select>
                        </div>

                        {/* Product search-as-you-type */}
                        <div className="md:col-span-2 relative">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Product</label>
                            <input
                                type="text"
                                placeholder={
                                    newItem.product_type
                                        ? `Search ${newItem.product_type}s...`
                                        : 'Select type first'
                                }
                                value={productSearch}
                                onChange={e => {
                                    setProductSearch(e.target.value);
                                    // Clear the confirmed selection when user re-types
                                    if (newItem.product_id) {
                                        setNewItem(prev => ({ ...prev, product_id: '', cost_price: 0 }));
                                    }
                                }}
                                disabled={!newItem.product_type}
                                className="form-input w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                autoComplete="off"
                            />

                            {/* Results dropdown */}
                            {productSearchResults.length > 0 && !newItem.product_id && (
                                <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                    {productSearchResults.map(product => (
                                        <li
                                            key={product.id}
                                            className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex justify-between items-center"
                                            onMouseDown={e => {
                                                // onMouseDown fires before onBlur so the click registers
                                                e.preventDefault();
                                                selectProduct(product);
                                            }}
                                        >
                                            <span className="font-medium">{product.name}</span>
                                            <span className="text-gray-400 text-xs ml-2">
                                                ${Number(product.cost_price).toFixed(2)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {productSearchLoading && (
                                <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                                    <Loader className="h-3 w-3 animate-spin" /> Searching...
                                </p>
                            )}

                            {!productSearchLoading && productSearch.length >= 2 && productSearchResults.length === 0 && (
                                <p className="mt-1 text-xs text-gray-400">No products found.</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>
                            <input
                                type="number"
                                name="quantity"
                                value={newItem.quantity}
                                onChange={handleItemChange}
                                className="form-input w-full rounded-md border-gray-300 text-sm"
                                min="1"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Cost Price</label>
                            <input
                                type="number"
                                name="cost_price"
                                value={newItem.cost_price}
                                onChange={handleItemChange}
                                className="form-input w-full rounded-md border-gray-300 text-sm"
                                step="0.01"
                                min="0"
                            />
                        </div>

                        <div className="flex items-end">
                            <button
                                type="button"
                                onClick={addItem}
                                className="w-full inline-flex items-center justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Items Table */}
                    {formData.items.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {formData.items.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900">{item.product_name}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs capitalize">
                                                    {item.product_type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                ${Number(item.cost_price).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                                ${item.total_price.toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(index)}
                                                    className="text-red-600 hover:text-red-900 p-1"
                                                    title="Remove item"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50">
                                    <tr>
                                        <td colSpan={4} className="px-4 py-3 text-sm font-medium text-gray-700 text-right">
                                            Total:
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            ${subtotal.toFixed(2)}
                                        </td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p>No items added yet. Use the form above to add products.</p>
                        </div>
                    )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => navigate('/purchases')}
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={submitting || formData.items.length === 0}
                    >
                        {submitting ? 'Creating...' : 'Create Purchase Order'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreatePurchase;
